/**
 * Design System - Typography System
 * 
 * Complete typography presets with colors, sizes, and weights.
 * Ready to use - no need to add colors separately.
 * 
 * Usage:
 *   import { typography } from '../design-system/tokens';
 *   <h1 className={typography.h1}>Title</h1>
 *   <h1 className={typography.h1Dark}>Title on dark background</h1>
 */

/**
 * COMPLETE Typography Classes
 * Everything included: size, weight, color, tracking
 * Use these directly - no extra styling needed
 */
export const typography = {
  // ========================================
  // HEADINGS - Light Background (Dark Text)
  // ========================================
  // All h1-h5 presets use the `font-heading` family (Nunito 700) defined in
  // tailwind.config.js + the `--font-heading` CSS variable in index.css.
  // To restyle every internal-UI title in one place, swap that variable.

  /** H1 - Main page title, hero heading */
  h1: 'font-heading text-4xl sm:text-5xl font-bold text-gray-900 dark:text-dark-50 tracking-tight',

  /** H2 - Major section title */
  h2: 'font-heading text-3xl sm:text-4xl font-bold text-gray-800 dark:text-dark-100 tracking-tight',

  /** H3 - Subsection title */
  h3: 'font-heading text-2xl font-bold text-gray-800 dark:text-dark-200 tracking-tight',

  /** H4 - Card title, minor section */
  h4: 'font-heading text-xl font-bold text-gray-800 dark:text-dark-200',

  /** H5 - Small heading */
  h5: 'font-heading text-lg font-bold text-gray-700 dark:text-dark-200',

  // ========================================
  // HEADINGS - Dark Background (Light Text)
  // ========================================

  /** H1 for dark backgrounds (navbar, hero, footer) */
  h1Dark: 'font-heading text-4xl sm:text-5xl font-bold text-white tracking-tight',

  /** H2 for dark backgrounds */
  h2Dark: 'font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight',

  /** H3 for dark backgrounds */
  h3Dark: 'font-heading text-2xl font-bold text-white tracking-tight',

  /** H4 for dark backgrounds */
  h4Dark: 'font-heading text-xl font-bold text-white',

  /** H5 for dark backgrounds */
  h5Dark: 'font-heading text-lg font-bold text-white',
  
  // ========================================
  // BODY TEXT - Light Background
  // ========================================
  
  /** Large body text - intros, emphasis */
  bodyLarge: 'text-lg text-gray-700 dark:text-dark-300 leading-relaxed',
  
  /** Normal body text - default paragraph */
  body: 'text-base text-gray-600 dark:text-dark-300 leading-relaxed',
  
  /** Small body text - secondary info */
  bodySmall: 'text-sm text-gray-600 dark:text-dark-300',
  
  /** Muted text - helper text, labels */
  muted: 'text-sm text-gray-500 dark:text-dark-400',
  
  // ========================================
  // BODY TEXT - Dark Background
  // ========================================
  
  /** Large body for dark backgrounds */
  bodyLargeDark: 'text-lg text-blue-100 leading-relaxed',
  
  /** Normal body for dark backgrounds */
  bodyDark: 'text-base text-blue-100 leading-relaxed',
  
  /** Small body for dark backgrounds */
  bodySmallDark: 'text-sm text-blue-200',
  
  /** Muted text for dark backgrounds */
  mutedDark: 'text-sm text-blue-300',
  
  // ========================================
  // SPECIAL PURPOSE
  // ========================================
  
  /** Links - clickable text */
  link: 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline font-medium transition-colors',
  
  /** Links on dark background */
  linkDark: 'text-blue-200 hover:text-white underline font-medium transition-colors',
  
  /** Caption - image captions, metadata */
  caption: 'text-xs text-gray-500 dark:text-dark-400 tracking-wide',
  
  /** Caption on dark background */
  captionDark: 'text-xs text-blue-300 tracking-wide',
  
  /** Label - form labels */
  label: 'text-sm font-medium text-gray-700 dark:text-dark-300',
  
  /** Overline - small all-caps labels */
  overline: 'text-xs font-semibold text-gray-600 dark:text-dark-400 tracking-widest uppercase',
  
  /** Error text */
  error: 'text-sm text-red-600 dark:text-red-300 font-medium',
  
  /** Success text */
  success: 'text-sm text-green-600 dark:text-green-300 font-medium',
  
  /** Button text - standard buttons */
  button: 'text-sm font-medium tracking-wide',
  
  /** Button text - large buttons */
  buttonLarge: 'text-base font-semibold tracking-wide',
};

/**
 * Font Families — Internal-UI (mirrors index.css CSS variables).
 *
 * The single source of truth lives in `src/index.css` as `--font-heading`
 * and `--font-body`, exposed as Tailwind utilities `font-heading` /
 * `font-body` (see tailwind.config.js).
 *
 * To restyle every internal-UI title at once, change `--font-heading` in
 * `index.css`. Do NOT hardcode font families in components.
 */
export const fontFamilies = {
  /** All internal-UI titles (h1-h5, h*Dark). Currently Nunito 700. */
  heading: 'var(--font-heading)',
  /** Body text, labels, buttons. System stack (unchanged). */
  body: 'var(--font-body)',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
};

/**
 * Default export
 */
export default typography;
